import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import {
    ResponsiveContainer, LineChart, Line, Tooltip as RTooltip,
    PieChart, Pie, Cell,
} from 'recharts';
import {
    fmtPct, fmtDelta, rateClass, RATE_COLORS, FAULT_COLORS, RECENT, ROLLING,
} from './format';
import { rolling, lastN, priorN, effectiveOk } from './aggregation';

const Card = ({ title, value, valueClass, sub, children }) => (
    <Box className="sh-card sh-card-tight" sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography className="sh-metric-label">{title}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mt: 0.5, gap: 1 }}>
            <Typography component="div" className={`sh-metric-value ${valueClass || ''}`} sx={{ fontSize: 24 }}>
                {value}
            </Typography>
            {sub && <Box sx={{ fontSize: 11, color: 'var(--color-text-secondary, #666)' }}>{sub}</Box>}
        </Box>
        <Box sx={{ mt: 1, flex: 1, minHeight: 80 }}>{children}</Box>
    </Box>
);

// — Card 1: trend sparkline (provider-run + client-night, 7d rolling)
const TrendSpark = ({ data, agg }) => {
    const N = 30;
    const start = Math.max(0, data.dates.length - N);
    const runR = rolling(agg.runRate, ROLLING).slice(start);
    const cnR = rolling(agg.clientNightRate, ROLLING).slice(start);
    const dates = data.dates.slice(start).map((d) => d.slice(5));
    const points = dates.map((date, i) => ({ date, run: runR[i], cn: cnR[i] }));

    const last7 = lastN(agg.runRate, 7);
    const prior7 = priorN(agg.runRate, 7);
    const delta = fmtDelta(last7 - prior7);

    return (
        <Card
            title={`Trend — last ${N}d, 7d rolling`}
            value={fmtPct(last7)}
            valueClass={`sh-${rateClass(last7)}`}
            sub={<span className={`sh-${delta.cls}`}>{delta.text} wow</span>}
        >
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={points} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                    <RTooltip
                        formatter={(v, n) => [v == null ? '—' : `${v.toFixed(1)}%`, n === 'run' ? 'Provider-run' : 'Client-night']}
                        labelFormatter={(d) => `${d}`}
                        contentStyle={{ fontSize: 11, padding: '4px 8px' }}
                    />
                    <Line type="monotone" dataKey="run" stroke={RATE_COLORS.good} strokeWidth={2} dot={false} connectNulls={false} />
                    <Line type="monotone" dataKey="cn" stroke="#e9a23b" strokeWidth={1.6} dot={false} connectNulls={false} />
                </LineChart>
            </ResponsiveContainer>
            <Box sx={{ display: 'flex', gap: 1.5, fontSize: 10, color: '#888', mt: 0.25 }}>
                <span><span style={{ color: RATE_COLORS.good, fontWeight: 700 }}>—</span> provider-run</span>
                <span><span style={{ color: '#e9a23b', fontWeight: 700 }}>—</span> client-night</span>
            </Box>
        </Card>
    );
};

// — Card 2: fault-mix donut (last 14d failed runs)
const FaultMixDonut = ({ agg }) => {
    const fb = agg.fb14;
    const slices = [
        { name: 'ours', value: fb.ours, color: FAULT_COLORS.ours },
        { name: 'shared', value: fb.shared, color: FAULT_COLORS.shared },
        { name: 'unknown', value: fb.unknown, color: FAULT_COLORS.unknown },
        { name: 'theirs', value: fb.theirs, color: FAULT_COLORS.theirs },
    ].filter((s) => s.value > 0);

    const ourPct = fb.total > 0 ? (fb.ours / fb.total) * 100 : 0;

    return (
        <Card
            title={`Failed runs — last ${RECENT}d`}
            value={fb.total.toLocaleString()}
            valueClass={fb.total === 0 ? 'sh-good' : 'sh-bad'}
            sub={fb.total > 0 ? <>{ourPct.toFixed(0)}% ours</> : 'all clean'}
        >
            {fb.total === 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#2a9d8f', fontSize: 12, fontWeight: 600 }}>
                    no failures
                </Box>
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Box sx={{ width: 96, height: 96 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={slices}
                                    dataKey="value"
                                    innerRadius={28}
                                    outerRadius={44}
                                    paddingAngle={1}
                                    stroke="none"
                                    isAnimationActive={false}
                                >
                                    {slices.map((s) => <Cell key={s.name} fill={s.color} />)}
                                </Pie>
                                <RTooltip
                                    formatter={(v, n) => [`${v} (${((v / fb.total) * 100).toFixed(0)}%)`, n]}
                                    contentStyle={{ fontSize: 11, padding: '4px 8px' }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>
                    <Box sx={{ flex: 1, ml: 1, fontSize: 11, lineHeight: 1.6 }}>
                        {slices.map((s) => (
                            <Box key={s.name} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                <span>
                                    <span style={{ display: 'inline-block', width: 8, height: 8, background: s.color, borderRadius: 2, marginRight: 6 }} />
                                    {s.name}
                                </span>
                                <span style={{ color: '#666', fontVariantNumeric: 'tabular-nums' }}>
                                    {s.value} ({((s.value / fb.total) * 100).toFixed(0)}%)
                                </span>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Card>
    );
};

// — Card 3: per-night status strip (last 14 nights)
const NightStatusStrip = ({ data, state }) => {
    const N = 14;
    const start = Math.max(0, data.runs.length - N);
    const nights = data.runs.slice(start).map((night, i) => {
        let failed = 0, total = 0;
        for (const r of night) {
            if (state.excludedClients.has(r[0]) || state.excludedProviders.has(r[1])) continue;
            total++;
            if (effectiveOk(r, state.faultMode) === 0) failed++;
        }
        return { date: data.dates[start + i], failed, total };
    });

    const cleanCount = nights.filter((n) => n.failed === 0).length;
    const cleanPct = nights.length > 0 ? (cleanCount / nights.length) * 100 : 0;

    const colorFor = (n) => {
        if (n.total === 0) return '#e0e0e0';
        if (n.failed === 0) return RATE_COLORS.good;
        if (n.failed <= 3) return RATE_COLORS.warn;
        return RATE_COLORS.bad;
    };

    return (
        <Card
            title={`Last ${N} nights`}
            value={`${cleanCount}/${nights.length}`}
            valueClass={`sh-${rateClass(cleanPct)}`}
            sub={`${cleanPct.toFixed(0)}% clean`}
        >
            <Box sx={{ display: 'flex', gap: '4px', alignItems: 'flex-end', height: '100%', pt: 0.5 }}>
                {nights.map((n) => {
                    const tip = n.total === 0
                        ? `${n.date} — no runs`
                        : n.failed === 0
                            ? `${n.date} — clean (${n.total} runs)`
                            : `${n.date} — ${n.failed}/${n.total} failed`;
                    // Bar height encodes failed share; clean nights show a thin baseline so the row reads as a strip.
                    const heightPct = n.total === 0
                        ? 8
                        : n.failed === 0
                            ? 18
                            : Math.min(100, 25 + (n.failed / n.total) * 75);
                    return (
                        <Tooltip key={n.date} title={tip} arrow>
                            <Box sx={{
                                flex: 1,
                                height: `${heightPct}%`,
                                minHeight: 6,
                                background: colorFor(n),
                                borderRadius: 1,
                                cursor: 'help',
                                transition: 'opacity 0.15s',
                                '&:hover': { opacity: 0.8 },
                            }} />
                        </Tooltip>
                    );
                })}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#888', mt: 0.5 }}>
                <span>{nights[0]?.date.slice(5) || ''}</span>
                <span>{nights[nights.length - 1]?.date.slice(5) || ''}</span>
            </Box>
        </Card>
    );
};

const OverviewRow = ({ data, agg, state }) => (
    <Box className="sh-tldr-cards">
        <TrendSpark data={data} agg={agg} />
        <FaultMixDonut agg={agg} />
        <NightStatusStrip data={data} state={state} />
    </Box>
);

export default OverviewRow;
